"use client";
import { useCallback, useEffect, useRef, useState } from "react";

export const VideoElement = ({
    src,
    className,
    ariaLabel,
    id,
    useIntersection = false,
    showFrame = false,
}: {
    id?: string;
    src: string;
    className?: string;
    ariaLabel?: string;
    useIntersection?: boolean;
    showFrame?: boolean;
}) => {
    if (!src) {
        return;
    }

    const observer = useRef<IntersectionObserver>(null);
    const observerVisible = useRef<IntersectionObserver>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(true);
    const [isVisible, setIsVisible] = useState(false);

    const setSound = () => {
        setIsMuted(videoRef.current?.muted || false);
    };

    useEffect(() => {
        videoRef.current?.addEventListener("volumechange", setSound);

        return () => {
            videoRef.current?.removeEventListener("volumechange", setSound);
        };
    }, [videoRef.current]);

    const toggleVideoSound = useCallback(() => {
        if (videoRef.current?.muted) {
            const elements = document.querySelectorAll("video");

            elements.forEach((el) => {
                el.muted = true;
            });

            videoRef.current!.muted = false;
        } else {
            videoRef.current!.muted = true;
        }
    }, []);

    useEffect(() => {
        if (isVisible) {
            if (videoRef.current) {
                videoRef.current.src = videoRef.current.getAttribute("data-src") || "";
            }
        }
    }, [isVisible]);

    useEffect(() => {
        const observerOptions = {
            root: null,
            rootMargin: "0px",
            threshold: 0.01,
        };

        observer.current = new IntersectionObserver(([entry]) => {
            setIsPlaying(entry.isIntersecting);
        }, observerOptions);
    }, []);

    useEffect(() => {
        const observerOptions = {
            root: null,
            rootMargin: "100px 0px 100px 0px",
            threshold: 0.01,
        };

        observerVisible.current = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setIsVisible(entry.isIntersecting);
            }
        }, observerOptions);
    }, []);

    useEffect(() => {
        if (isPlaying) {
            videoRef.current?.play();
        } else {
            videoRef.current?.pause();
        }
    }, [isPlaying]);

    useEffect(() => {
        if (videoRef.current) {
            observer.current?.observe(videoRef.current);
            observerVisible.current?.observe(videoRef.current);
        }
        return () => {
            observer.current?.disconnect();
            observerVisible.current?.disconnect();
        };
    }, []);

    const videoElement = (
        <>
            <video
                ref={videoRef}
                id={id}
                data-src={src}
                muted
                autoPlay={!useIntersection}
                loop
                playsInline
                className={className}
                aria-label={ariaLabel}
            />

            {showFrame && (
                <button className="absolute top-9 left-7.5" onClick={() => toggleVideoSound()}>
                    {isMuted ? (
                        <img src="/icon_mute.svg" alt="Mute Video" />
                    ) : (
                        <img src="/icon_unmute.svg" alt="Unmute Video" />
                    )}
                </button>
            )}
        </>
    );

    // Return with optional wrapper
    if (showFrame) {
        return <div className="relative">{videoElement}</div>;
    }

    return videoElement;
};
